import { useState, useEffect } from 'react';
import { Check, X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { createPayment, fetchPlans, cancelPlan, collectFeedback } from '../../services/api';
import { createAnalyticsFlowId, trackProductEvent } from '../../services/analytics';
import { getEffectivePlanName } from './planPrompts';
import PlusTrialSurvey from './PlusTrialSurvey';

const PAYMENT_MEASUREMENT_BASELINE = 'baseline';
const PLUS_TRIAL_CAMPAIGN = 'three_alias_plus_trial';

interface PlanDetailsProps {
  isOpen: boolean;
  onClose: () => void;
}

const Pricing = ({ isOpen, onClose }: PlanDetailsProps) => {
  const { t, i18n } = useTranslation();
  const { userInfo } = useUser();
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [maxthonModalOpen, setMaxthonModalOpen] = useState(false);
  const campaign = new URLSearchParams(window.location.search).get('campaign') === PLUS_TRIAL_CAMPAIGN
    ? PLUS_TRIAL_CAMPAIGN
    : undefined;

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await fetchPlans();
      let rawPlans = response;
      if (response?.code === 0) {
        rawPlans = response.data || response.result;
      }

      if (rawPlans) {
        // Transform object response to array
        const plansArray = Object.entries(rawPlans)
          .filter(([key, value]: [string, any]) => key.startsWith('uumail_') && typeof value === 'object')
          .map(([key, value]: [string, any]) => {
            const priceString = String(value.price || '');
            const [priceStr, cycleStr] = priceString.split('|');
            return {
              ...value,
              id: key,
              price: priceStr ? parseInt(priceStr, 10) : 0,
              type: cycleStr === 'M' ? 'month' : 'year',
              level: key.includes('plus') ? 'plus' : 'pro'
            };
          });
        setPlans(plansArray);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;

  const getCurrentPlan = () => {
    return getEffectivePlanName(userInfo);
  };

  const currentPlan = getCurrentPlan();
  const planStatus = userInfo?.plan_status;
  const formatPrice = (price: number) => `$${(price / 100).toFixed(2)}`;

  const handleCancelPlan = async () => {
    setCanceling(true);
    try {
      const response = await cancelPlan();
      if (!response.err) {
        trackProductEvent('plan_cancel_success', {
          source: 'plan_details',
          userId: userInfo?.uid,
          to: currentPlan,
          reason: planStatus || 'active'
        });
        window.location.reload();
      } else {
        trackProductEvent('plan_cancel_error', {
          source: 'plan_details',
          userId: userInfo?.uid,
          to: currentPlan,
          reason: response.err
        });
        console.error('Failed to cancel plan:', response.err);
        setCanceling(false);
        setCancelModalOpen(false);
      }
    } catch (error) {
      trackProductEvent('plan_cancel_error', {
        source: 'plan_details',
        userId: userInfo?.uid,
        to: currentPlan,
        reason: 'exception'
      });
      console.error('Failed to cancel plan:', error);
      setCanceling(false);
      setCancelModalOpen(false);
    }
  };

  const handlePlanSwitch = async (plan: any) => {
    if (userInfo?.plan?.mxPlan) {
      trackProductEvent('plan_maxthon_manage_open', {
        source: 'plan_details',
        userId: userInfo?.uid,
        to: plan.id,
        reason: 'mxPlan'
      });
      setMaxthonModalOpen(true);
      return;
    }

    if (plan.level === currentPlan) {
        if (plan.level !== 'free' && planStatus !== 'cancelled') {
            trackProductEvent('plan_cancel_open', {
              source: 'plan_details',
              userId: userInfo?.uid,
              to: plan.id,
              reason: planStatus || 'active'
            });
            setCancelModalOpen(true);
            return;
        }
    }

    const checkoutFlowId = createAnalyticsFlowId('checkout');

    trackProductEvent('plan_checkout_start', {
      source: 'plan_details',
      userId: userInfo?.uid,
      flowId: checkoutFlowId,
      to: plan.id,
      reason: planStatus || currentPlan,
      paymentMeasurement: PAYMENT_MEASUREMENT_BASELINE,
      ...(campaign ? { campaign } : {})
    });

    try {
      const response = campaign && plan.id === 'uumail_plus'
        ? await createPayment(plan, campaign)
        : await createPayment(plan);
      if (response.url) {
        trackProductEvent('plan_checkout_redirect', {
          source: 'plan_details',
          userId: userInfo?.uid,
          flowId: checkoutFlowId,
          to: plan.id,
          reason: 'url',
          paymentMeasurement: PAYMENT_MEASUREMENT_BASELINE,
          ...(campaign ? { campaign } : {})
        });
        window.location.href = response.url;
      } else if (response.err) {
        trackProductEvent('plan_checkout_error', {
          source: 'plan_details',
          userId: userInfo?.uid,
          flowId: checkoutFlowId,
          to: plan.id,
          reason: response.err,
          paymentMeasurement: PAYMENT_MEASUREMENT_BASELINE,
          ...(campaign ? { campaign } : {})
        });
        console.error('Failed to initiate payment:', response.err);
      } else {
        trackProductEvent('plan_checkout_no_url', {
          source: 'plan_details',
          userId: userInfo?.uid,
          flowId: checkoutFlowId,
          to: plan.id,
          reason: 'missing-url',
          paymentMeasurement: PAYMENT_MEASUREMENT_BASELINE,
          ...(campaign ? { campaign } : {})
        });
      }
    } catch (error) {
      trackProductEvent('plan_checkout_error', {
        source: 'plan_details',
        userId: userInfo?.uid,
        flowId: checkoutFlowId,
        to: plan.id,
        reason: 'exception',
        paymentMeasurement: PAYMENT_MEASUREMENT_BASELINE,
        ...(campaign ? { campaign } : {})
      });
      console.error('Failed to initiate payment:', error);
    }
  };

  const handleCampaignFeedback = async (payload: Parameters<typeof collectFeedback>[0]) => {
    try {
      const result = await collectFeedback(payload);
      trackProductEvent('campaign_survey_submitted', {
        source: 'plan_details',
        userId: userInfo?.uid,
        campaign: PLUS_TRIAL_CAMPAIGN,
        desiredAliasCount: payload.metadata?.desired_alias_count
      });
      return result;
    } catch (error) {
      trackProductEvent('campaign_survey_error', {
        source: 'plan_details',
        userId: userInfo?.uid,
        campaign: PLUS_TRIAL_CAMPAIGN,
        reason: 'feedback-unavailable'
      });
      throw error;
    }
  };

  const plusPlan = plans.find((plan) => plan.id === 'uumail_plus') || {
    id: 'uumail_plus',
    level: 'plus',
    type: 'month',
    price: 399
  };
  const proPlan = plans.find((plan) => plan.id === 'uumail_pro') || {
    id: 'uumail_pro',
    level: 'pro',
    type: 'month',
    price: 999
  };
  const paidPlans = [
    {
      plan: plusPlan,
      name: t('landing.pricing.plans.plus.name'),
      description: t('landing.pricing.plans.plus.description'),
      popular: true,
      features: [
        t('landing.pricing.features.unlimitedAliases'),
        t('landing.pricing.features.emailsPerMonth', { count: 100 }),
        t('landing.pricing.features.replyFromAlias'),
        t('landing.pricing.features.sendFromAlias')
      ]
    },
    {
      plan: proPlan,
      name: t('landing.pricing.plans.pro.name'),
      description: t('landing.pricing.plans.pro.description'),
      popular: false,
      features: [
        t('landing.pricing.features.unlimitedAliases'),
        t('landing.pricing.features.emailsPerMonth', { count: 1000 }),
        t('landing.pricing.features.replyFromAlias'),
        t('landing.pricing.features.sendFromAlias'),
        t('landing.pricing.features.analytics')
      ]
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 right-0 z-10 bg-white flex justify-end p-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="px-4 md:px-6 pb-8 pt-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.pricing.upgradeTitle')}
            </h2>
            <p className="text-base text-gray-700 max-w-xl mx-auto">
              {t('landing.pricing.upgradeSubtitle')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="mx-auto grid max-w-4xl gap-5 md:grid-cols-2">
              {paidPlans.map(({ plan, name, description, features, popular }) => {
                const isCurrent = currentPlan === plan.level;
                const isCancelled = isCurrent && planStatus === 'cancelled';
                const actionLabel = isCurrent
                  ? (isCancelled ? t('landing.pricing.reactivate') : t('landing.pricing.cancel'))
                  : (campaign && plan.id === 'uumail_plus'
                    ? t('plusTrialSurvey.startTrial')
                    : (plan.level === 'plus' ? t('landing.pricing.unlockPlus') : t('landing.pricing.buttons.choosePro')));

                return (
                  <div
                    key={plan.id}
                    className={`rounded-xl border-2 bg-white shadow-xl ${popular ? 'border-blue-500' : 'border-slate-300'}`}
                  >
                    {popular && (
                      <div className="bg-blue-500 text-white text-center py-1.5 px-4 rounded-t-lg text-sm font-medium">
                        {t('landing.pricing.mostPopular')}
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {name}
                      </h3>
                      <p className="text-sm text-gray-700 mb-5">
                        {description}
                      </p>
                      {userInfo?.plan?.mxPlan && currentPlan === plan.level && (
                        <div className="mb-4 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {t('landing.pricing.maxthonPlan')}
                        </div>
                      )}
                      <div className="mb-5">
                        <span className="text-4xl font-bold text-gray-900">{formatPrice(plan.price)}</span>
                        <span className="text-gray-600">{t('landing.pricing.perMonth')}</span>
                      </div>
                      <ul className="space-y-3 mb-6">
                        {features.map((feature) => (
                          <li key={feature} className="flex items-start text-sm">
                            <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mb-6 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        <p className="font-medium text-slate-900">{t('landing.pricing.privacyTitle')}</p>
                        <p>{t('landing.pricing.privacySubtitle')}</p>
                      </div>
                      {campaign && plan.id === 'uumail_plus' && !isCurrent && (
                        <p className="mb-4 text-sm text-slate-700">
                          {t('plusTrialSurvey.renewalDisclosure', { price: formatPrice(plan.price) })}
                        </p>
                      )}
                      <button
                        onClick={() => handlePlanSwitch(plan)}
                        className={`block w-full py-3 px-4 rounded-md text-center font-semibold transition-colors ${
                          isCurrent && planStatus !== 'cancelled'
                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                            : popular
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`}
                      >
                        {actionLabel}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {campaign && !loading && (
            <PlusTrialSurvey onSubmit={handleCampaignFeedback} />
          )}
        </div>
      </div>
      {/* Cancel Confirmation Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setCancelModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="flex items-center justify-center mb-4 text-amber-500">
              <AlertTriangle className="h-12 w-12" />
            </div>
            <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
              {t('landing.pricing.cancelConfirmTitle')}
            </h3>
            <p className="text-gray-600 text-center mb-6">
              {t('landing.pricing.cancelConfirmDesc')}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setCancelModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                disabled={canceling}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCancelPlan}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium flex justify-center items-center"
                disabled={canceling}
              >
                {canceling ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('landing.pricing.canceling')}
                  </>
                ) : (
                  t('landing.pricing.confirmCancel')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {maxthonModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8 relative">
            <button
              onClick={() => setMaxthonModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              {t('landing.pricing.maxthonManageTitle')}
            </h3>
            <p className="text-gray-600 text-lg mb-10">
              {t('landing.pricing.maxthonManageDesc')}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  const url = i18n.language?.startsWith('zh')
                    ? 'https://www.maxthon.com/zh/vip-new'
                    : 'https://www.maxthon.com/en/vip-new';
                  window.open(url, '_blank', 'noopener,noreferrer');
                  setMaxthonModalOpen(false);
                }}
                className="flex-1 bg-emerald-500 text-white py-4 rounded-xl font-semibold text-xl hover:bg-emerald-600 transition-colors"
              >
                {t('landing.pricing.openMaxthonVip')}
              </button>
              <button
                onClick={() => setMaxthonModalOpen(false)}
                className="flex-1 bg-gray-100 text-gray-900 py-4 rounded-xl font-semibold text-xl hover:bg-gray-200 transition-colors"
              >
                {t('landing.pricing.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
